<template>
  <v-container fluid>
    <!-- Header -->
    <v-row>
      <v-col>
        <v-card>
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <v-card-title> Case Reports </v-card-title>
              <v-card-subtitle>
                {{ getOrg }}
              </v-card-subtitle>
            </v-col>
            <v-col align="right">
              <v-btn
                class="mx-2 mr-4"
                fab
                dark
                x-small
                :loading="tableLoading"
                color="primary"
                @click="getCaseReports(true)"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- CaseReportTable -->
    <!-- filters -->
    <v-row dense>
      <v-col>
        <v-card outlined>
          <v-card-text>
            <v-row
              dense
              align="center"
            >
              <v-col cols="3">
                <v-text-field
                  label="Case Name"
                  v-model="caseName"
                  dense
                  clearable
                  class="text-body-1"
                />
              </v-col>
              <!-- Local ID  -->
              <v-col cols="2">
                <v-text-field
                  label="ID"
                  v-model="localId"
                  dense
                  clearable
                  class="text-body-1"
                />
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="assignedTo"
                  :items="users"
                  item-value="email"
                  :item-text="
                    (item) => {
                      return getTextForUserSelect(item);
                    }
                  "
                  label="Assignee"
                  clearable
                  dense
                  class="text-body-1"
                />
              </v-col>
              <v-col cols="3">
                <v-select
                  dense
                  label="Status"
                  v-model="statusFilter"
                  :items="getReportStatuses"
                  item-text="status"
                  item-value="code"
                  clearable
                  multiple
                  class="text-body-1"
                />
              </v-col>
            </v-row>
            <v-row dense>
              <!-- Case Group -->
              <v-col cols="4">
                <v-select
                  dense
                  label="Group"
                  v-model="caseGroups"
                  :items="getCaseGroups"
                  item-text="name"
                  return-object
                  clearable
                  multiple
                  class="text-body-1"
                />
              </v-col>
              <!-- screened on -->
              <v-col cols="3">
                <v-menu
                  ref="screeningDateRange"
                  v-model="screeningDateRange"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  class="text-body-1"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dateRangeFormattedComputed"
                      label="Report Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      dense
                      clearable
                      @click:clear="dates = []"
                      class="text-body-1"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dates"
                    range
                    no-title
                  >
                    <v-spacer />
                    <v-btn
                      text
                      color="primary"
                      @click="screeningDateRange = false; dates = []"
                      
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="$refs.screeningDateRange.save(dates)"
                    >
                      OK
                    </v-btn>
                  </v-date-picker>
                </v-menu>
              </v-col>

              <v-spacer />
              <v-col
                align="right"
                cols="1"
              >
                <v-btn
                  fab
                  dark
                  x-small
                  @click="search()"
                >
                  <v-icon small>mdi-magnify</v-icon>
                </v-btn>
                <!-- TODO put in refresh button -->
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- table -->
    <v-row dense>
      <v-col>
        <v-data-table
          dense
          :headers="headers"
          :items="caseReports"
          :options.sync="options"
          :server-items-length="totalCaseReports"
          :footer-props="footerProps"
          disable-sort
          v-model="multiSelectedReports"
          item-key="id"
          :loading="tableLoading"
          show-select
          no-data-text="All cases resolved"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-spacer />
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="primary"
                    dark
                    v-bind="attrs"
                    v-on="on"
                  >
                    Actions
                    <v-icon
                      dark
                      right
                    >
                      mdi-chevron-down
                    </v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <!-- Multi Assign -->
                  <v-list-item
                    link
                    @click="onMultiAssignClick()"
                    :disabled="multiSelectedReports.length < 1 || !hasPermission('CAN_MODIFY_REPORTS')"
                  >
                    <v-list-item-icon>
                      <v-icon> mdi-account-switch </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Assign Selected</v-list-item-title>
                      <v-list-item-subtitle>Assign selected cases</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item
                    link
                    @click="onCsvExport()"
                    :disabled="totalCaseReports < 1"
                  >
                    <v-list-item-icon>
                      <v-icon> mdi-download </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Export Report History</v-list-item-title>
                      <v-list-item-subtitle>Export a CSV of Case Reports based on current search parameters</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-toolbar>
          </template>

          <template v-slot:item.matches="{ item }">
            <v-chip
              small
              outlined
              v-if="item.matches.length > 0"
              class="ma-2"
              color="red"
              text-color="red"
              >{{ item.matches.length }}</v-chip
            >
            <v-chip
              small
              outlined
              v-else
              class="ma-2"
              color="green"
              text-color="green"
              >0</v-chip
            >
          </template>

          <template v-slot:item.status="{ item }">
            <ResolutionStatusChips :resolutionStatus="item.status" />
          </template>

          <template v-slot:item.caseGroupId="{ item }">
            {{ item.caseRecord.caseGroupId == null || item.caseRecord.caseGroupId === undefined ? "" : getCaseGroupName(item.caseRecord.caseGroupId) }}
          </template>

          <template v-slot:item.reportDate="{ item }">
            {{ formatDateTime(item.reportDate) }}
          </template>

          <template v-slot:item.comments="{ item }">
            {{ item.comments.length > 0 ? item.comments.length : "" }}
          </template>

          <!-- <template v-slot:item.assignee="{ item }">
            <span v-if="item.assignee">
              {{ getNameForEmail(item.assignee) }}
            </span>
          </template> -->

          <template v-slot:item.controls="{ item }">
            <v-btn
              icon
              x-small
              color="primary"
              @click="openCaseReport(item)"
            >
              <v-icon dark>mdi-export</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <BulkAssign
      :visible="multiAssignVisible"
      :loading="multiAssignLoading"
      :totalSelectedCases="multiSelectedReports.length"
      v-on:onConfirm="onMultiAssignConfirmed"
      v-on:onCancelled="onMultiAssignCancelled"
    />

    <!-- Case Report Dialog -->
    <v-dialog v-model="caseReportDialog">
      <v-sheet>
        <CaseReport
          :selectedCase="selectedCaseReport.caseRecord"
          :selectedCaseReport="selectedCaseReport"
          v-on:onClose="onCaseReportClose"
          v-on:caseReportUpdated="caseReportUpdated"
        />
      </v-sheet>
    </v-dialog>
  </v-container>
</template>

<script>
import { getCaseReportApi, getCaseReportsApi, setAssigneeBulkApi, downloadCaseReportCsvApi } from "../api/caseReportApi";
import { hasPermission } from "../utils/permission";
import { mapGetters, mapActions } from "vuex";
import CaseReport from "./CaseReport.vue";
// import MultiResolution from "../modals/case reports/MultiResolution";
import BulkAssign from "../modals/case reports/BulkAssign.vue";
import { getTextForUserSelect } from "../utils/userUtils";
import ResolutionStatusChips from "../components/ResolutionStatusChips";
import { getCaseGroupName } from "../utils/caseGroupUtils";
import { formatDateTime, dateRangeFormatted } from "../utils/dateUtils";

export default {
  name: "Resolution",
  components: { /*MultiResolution,*/ BulkAssign, CaseReport, ResolutionStatusChips },
  computed: {
    ...mapGetters(["getOrg", "getUserProfile", "getAccount", "users", "getPreferences", "getCaseGroups", "getReportStatuses"]),
    dateRangeFormattedComputed: {
      get: function () {
        return this.dateRangeFormatted(this.dates[0], this.dates[1]);
      },
      set: function (newDates) {
        newDates;
      },
    },
  },
  props: {
    id: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      // table data
      options: {},
      footerProps: { "items-per-page-options": [10, 20, 100, 200] },
      totalCaseReports: 0,
      caseReports: [],

      tableLoading: false,
      caseReportDialog: false,
      selectedCaseReport: {},
      multiSelectedReports: [],
      searchSummaryVisible: false,
      caseReportVisible: false,
      // multiResolutionVisible: false,
      // multiResolutionLoading: false,
      multiAssignVisible: false,
      multiAssignLoading: false,
      headers: [
        { text: "Case", value: "caseRecord.name" },
        { text: "ID", value: "caseRecord.localId" },
        { text: "Group", value: "caseGroupId" },
        { text: "Report Date", value: "reportDate" },
        { text: "Assignee", value: "assignee" },
        { text: "Comments", value: "comments" },
        { text: "Status", value: "status" },
        { text: "View", value: "controls", sortable: false, align: "right" },
      ],

      // search/filter
      caseName: "",
      localId: "",
      caseGroups: [],
      statusFilter: ["OPEN"],
      screenedBy: null,
      assignedTo: this.$store.getters["getPreferences"].filters.resolution.assignedTo === undefined ? this.$store.getters["getUserProfile"].email : this.$store.getters["getPreferences"].filters.resolution.assignedTo,
      screeningDateRange: false,
      dates: [],
    };
  },
  async mounted() {
    if (this.id) {
      // console.log("ID passed to Resolution: " + this.id);
      this.loading = true;
      try {
        const response = await this.getCaseReportApi(this.id);
        this.openCaseReport(response);
      } catch (error) {
        console.error("Error fetching case report by ID (path)", error);
        this.setGlobalAlert({
          alert: true,
          color: "red",
          alertTitle: "Oops.",
          alertMessage: "Unable to get case reports at this time. Please try again later.",
        });
      } finally {
        this.selectedCases = [];
        this.loading = false;
      }
    }
  },
  watch: {
    options: {
      handler() {
        this.getCaseReports(false);
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions(["setGlobalAlert", "fetchCaseGroups", "resolveCaseReports", "assignCaseReports", "setResolutionFilter", "assignCaseReports"]),
    hasPermission,
    getCaseReportApi,
    getCaseReportsApi,
    setAssigneeBulkApi,
    downloadCaseReportCsvApi,
    getTextForUserSelect,
    getCaseGroupName,
    formatDateTime,
    dateRangeFormatted,
    created() {},
    search() {
      this.setResolutionFilter({ assignedTo: this.assignedTo }); // This saves the user's assignee preference.
      this.getCaseReports(true);
    },
    async onCsvExport() {
      if (this.totalCaseReports > 5000) {
        this.setGlobalAlert({
          alert: true,
          alertTitle: "Maximum Case Reports",
          alertMessage: "Please narrow your search criteria to proceed with CSV export.",
        });
        return;
      } else {
        this.tableLoading = true;
        try {
          var params = this.getSearchParams();
          await this.downloadCaseReportCsvApi(params);
        } catch (error) {
          this.setGlobalAlert({
            alert: true,
            color: "error",
            alertTitle: "Error",
            alertMessage: "There's been an error generating the CSV report. Please try again shortly.",
          });
        } finally {
          this.tableLoading = false;
        }
      }
    },
    async getCaseReports(resetPage) {
      this.tableLoading = true;
      var request = this.getSearchParams();
      Object.assign(request, this.options);
      request.page = request.page - 1;

      try {
        const response = await this.getCaseReportsApi(request);
        this.caseReports = response.caseReports;
        this.totalCaseReports = response.totalCaseReports;
        if (resetPage) {
          this.options.page = 1;
        }
        this.tableLoading = false;
      } catch (error) {
        this.tableLoading = false;
        this.setGlobalAlert({
          alert: true,
          color: "red",
          alertTitle: "There's been a problem.",
          alertMessage: "Unable to retreive reports, please try again later.",
        });
      }
    },
    getSearchParams() {
      var request = {};

      if (this.screenedBy) request.screenedBy = this.screenedBy;
      if (this.assignedTo) request.assignedTo = this.assignedTo;
      if (this.caseName) request.caseName = this.caseName;
      if (this.localId) request.localId = this.localId;
      if (this.dates[0]) request.screenedAfter = this.dates[0];
      if (this.dates[1]) request.screenedBefore = this.dates[1];
      if (this.statusFilter) {
        request.status = this.statusFilter;
      }
      if (this.caseGroups) {
        var groupIds = [];
        for (let cG in this.caseGroups) {
          groupIds.push(this.caseGroups[cG].id);
        }
        request.groupId = groupIds;
      }
      return request;
    },
    openCaseReport(selectedCaseReport) {
      this.selectedCaseReport = selectedCaseReport;
      this.caseReportDialog = true;
    },
    caseReportUpdated(updatedCaseReport) {
      const index = this.caseReports.findIndex((caseReport) => updatedCaseReport.id === caseReport.id);
      this.$set(this.caseReports, index, updatedCaseReport); // This is required so the change is picked up by the watcher in ViewCase when an update comes in from CaseReport modal.
      this.selectedCaseReport = updatedCaseReport;
    },
    onCaseReportClose() {
      this.caseReportDialog = false;
      // this.selectedCaseReport = {}; // For some reason this causes an error in the CaseReport diaglog when it tries to read object properties like .match I am not sure why it doesn't case the error initially in the component.
    },
    onMultiAssignClick() {
      this.multiAssignVisible = true;
    },
    async onMultiAssignConfirmed(assignee) {
      this.multiAssignLoading = true;

      let caseReportIds = this.multiSelectedReports.map(function (aCase) {
        return aCase.id;
      });

      try {
        await this.setAssigneeBulkApi(caseReportIds, assignee).then((updated) => {
          this.setGlobalAlert({
            alert: true,
            color: "black",
            alertTitle: `${updated} reports assigned to ${assignee}`,
          });
          this.multiSelectedReports = [];
          this.multiAssignVisible = false;
          this.multiAssignLoading = false;
          this.getCaseReports(true);
        });
      } catch (error) {
        console.error("an error", error);
        this.setGlobalAlert({
          alert: true,
          color: "red",
          alertTitle: "Oops.",
          alertMessage: this.$t("error.general.unhandled"),
        });
        this.multiAssignLoading = false;
      }
    },
    onMultiAssignCancelled() {
      this.multiAssignVisible = false;
    },
  },
};
</script>
